import { useCarriersQuery, useProductsQuery } from "@qw/graphql";
import { FieldCombobox, FieldRadioGroup } from "@qw/ui";
import { type FC, useMemo } from "react";

interface ProductMetadataFieldsProps {
  canEdit: boolean;
  hasExistingCarrier?: boolean;
}

export const ProductMetadataFields: FC<ProductMetadataFieldsProps> = ({ canEdit, hasExistingCarrier }) => {
  const { data: carriersData, loading: carriersLoading } = useCarriersQuery();
  // For product management we only want LOBs that actually exist on products
  // (this matches the Airtable-synced/app-visible set, instead of showing every seeded LOB).
  const { data: productsData, loading: productsLoading } = useProductsQuery();

  const carriers = carriersData?.carriers ?? [];
  const linesOfBusiness = useMemo(() => {
    const byId = new Map<string, { id: string; displayName: string; code?: string | null }>();

    for (const product of productsData?.products ?? []) {
      for (const lob of product.linesOfBusiness ?? []) {
        if (!lob?.id || !lob.displayName) {
          continue;
        }
        byId.set(lob.id, { id: lob.id, displayName: lob.displayName, code: lob.code });
      }
    }

    return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [productsData]);

  // Disable carrier field if carrier is already set (cannot be changed after creation)
  const isCarrierDisabled = !canEdit || carriersLoading || hasExistingCarrier;

  return (
    <>
      <FieldCombobox
        label="Carrier"
        name="carrierId"
        disabled={isCarrierDisabled}
        loading={carriersLoading}
        placeholder={
          carriersLoading
            ? "Loading carriers..."
            : hasExistingCarrier
              ? "Carrier cannot be changed"
              : "Select a carrier"
        }
        options={carriers.map((carrier) => ({
          label: carrier.name,
          value: carrier.slug,
        }))}
      />

      <FieldCombobox
        label="Lines of Business"
        name="lineOfBusinessIds"
        disabled={!canEdit || productsLoading}
        loading={productsLoading}
        placeholder={productsLoading ? "Loading lines of business..." : "Select lines of business"}
        multiple={true}
        options={linesOfBusiness.map((lob) => ({
          label: lob.displayName,
          value: lob.id,
          keywords: lob.code ? [lob.code] : undefined,
        }))}
      />

      <FieldRadioGroup
        label="Market Type"
        name="marketType"
        disabled={!canEdit}
        options={[
          {
            label: "Binding",
            value: "bindingAuthority",
          },
          {
            label: "Brokerage",
            value: "brokerage",
          },
        ]}
        inputProps={{ className: "flex flex-row gap-4" }}
      />
    </>
  );
};
